import { atom } from "jotai";

import { generateShortUuid } from "@moment/api-collab";
import type { PageMetadata } from "@moment/api-collab/api-types";
import { Logger } from "@moment/logging";

import { $accessTokenFunc } from "../authenication";
import { $docsApi } from "../docsapi";
import { metadataKeyForDocsRoute } from "../document-metadata/documentMetadataKey";
import { $documentMetadata } from "../document-metadata/metadata-state.atom";
import { isAgentInstructionPage } from "../document/agents-md.atom";
import { $isDocumentPublished } from "../document/sharing/permissions.atom";
import { $createPage } from "../page";
import { isValidTemplateId } from "./validation";

const logger = new Logger("import-template-pages");

const TEMPLATE_WORKSPACE = "moment-templates";

export interface ImportTemplatePagesInput {
	documentId: string;
	templateId: string;
	/** Optional parent page ID. When provided, template root pages become children of this page. */
	parentPageID?: string;
}

/**
 * Imports all pages from a template into an existing document.
 *
 * For each page in the template:
 * - Generates a new unique page ID and slug
 * - Preserves the page title, type, and content
 * - Remaps parent-child page relationships to use the new IDs
 * - Fetches and imports the page content (markdown, HTTP request, or empty canvas)
 *
 * Returns the array of newly created PageMetadata objects.
 */
export const $importTemplatePages = atom(
	null,
	async (get, set, input: ImportTemplatePagesInput): Promise<PageMetadata[]> => {
		const { documentId, templateId, parentPageID } = input;

		logger.info("Importing template pages", { documentId, templateId, parentPageID });

		// Validate template ID
		if (!isValidTemplateId(templateId)) {
			throw new Error(`Invalid template ID: ${templateId}`);
		}

		// Get auth token
		const { getAccessTokenSilently } = get($accessTokenFunc);
		const token = await getAccessTokenSilently?.();

		// Fetch template metadata
		const docsApi = get($docsApi);
		const templateMetadata = await docsApi.document.metadata.read(
			{ workspaceName: TEMPLATE_WORKSPACE, documentId: templateId },
			token
		);

		if (!templateMetadata) {
			throw new Error(`Template metadata not found: ${templateId}`);
		}

		const templatePages = templateMetadata.pagesMetadata;
		if (templatePages.length === 0) {
			logger.warn("Template has no pages to import", { templateId });
			return [];
		}

		// Block importing templates with request pages into published documents
		const isPublished = await get($isDocumentPublished(documentId));
		const hasRequestPages = templatePages.some((p) => p.type === "request");
		if (isPublished && hasRequestPages) {
			throw new Error("Cannot import templates with request pages into a published document");
		}

		logger.debug("Template has pages to import", {
			templateId,
			pageCount: templatePages.length,
		});

		// Build a mapping from old page IDs to new page IDs
		const oldIdToNewId = new Map<string, string>();
		for (const page of templatePages) {
			oldIdToNewId.set(page.id, generateShortUuid());
		}

		// Get current document page count for pageIndex offset
		const metadataKey = metadataKeyForDocsRoute(documentId);
		const currentMetadata = get($documentMetadata(metadataKey));
		const existingPageCount = currentMetadata?.pagesMetadata?.length ?? 0;

		// Import each page sequentially to maintain order
		const createdPages: PageMetadata[] = [];

		for (let i = 0; i < templatePages.length; i++) {
			const templatePage = templatePages[i];

			// Skip root-level agent instruction files (AGENTS.md, CLAUDE.md) —
			// they are auto-discovered from disk and shown in the Misc sidebar section.
			if (isAgentInstructionPage(templatePage.path)) {
				logger.debug("Skipping agent instruction page during template import", {
					templateId,
					pageId: templatePage.id,
					path: templatePage.path,
				});
				continue;
			}

			const newPageId = oldIdToNewId.get(templatePage.id)!;

			// Remap parentPageID to new ID if it exists within the template,
			// otherwise use the provided parentPageID for root-level template pages
			const remappedParentPageID = templatePage.parentPageID
				? (oldIdToNewId.get(templatePage.parentPageID) ?? null)
				: (parentPageID ?? null);

			const pageMetadata: Partial<PageMetadata> = {
				id: newPageId,
				slug: newPageId,
				title: templatePage.title,
				parentPageID: remappedParentPageID,
			};

			const pageType = templatePage.type ?? "markdown";
			const pageIndex = existingPageCount + i;

			try {
				if (pageType === "markdown") {
					// Fetch raw markdown content from template
					let rawMarkdown: string | undefined;
					try {
						rawMarkdown = await docsApi.document.page.readRaw(
							{
								workspaceName: TEMPLATE_WORKSPACE,
								documentId: templateId,
								pageId: templatePage.id,
								ref: "main",
							},
							token
						);
					} catch (err) {
						logger.warn("Failed to fetch template page content, creating empty page", {
							templateId,
							pageId: templatePage.id,
							err,
						});
					}

					const createdPage = await set($createPage, {
						documentId,
						pageType: "markdown",
						pageMetadata,
						pageIndex,
						...(rawMarkdown && {
							initialPageContents: rawMarkdown,
							bodyType: "markdown" as const,
						}),
					});
					createdPages.push(createdPage);
				} else if (pageType === "request") {
					// Fetch HTTP request content from template
					let requestContent;
					try {
						requestContent = await docsApi.document.page.readHttpRequest(
							{
								workspaceName: TEMPLATE_WORKSPACE,
								documentId: templateId,
								pageId: templatePage.id,
								pagePath: templatePage.path,
								ref: "main",
							},
							token
						);
					} catch (err) {
						logger.warn(
							"Failed to fetch template request page content, creating default",
							{
								templateId,
								pageId: templatePage.id,
								err,
							}
						);
					}

					const createdPage = await set($createPage, {
						documentId,
						pageType: "request",
						pageMetadata,
						pageIndex,
						...(requestContent && {
							initialRequestPageContents: requestContent,
						}),
					});
					createdPages.push(createdPage);
				} else if (pageType === "canvas") {
					// Canvas pages are created empty
					const createdPage = await set($createPage, {
						documentId,
						pageType: "canvas",
						pageMetadata,
						pageIndex,
					});
					createdPages.push(createdPage);
				} else {
					logger.warn("Unknown page type, skipping", {
						templateId,
						pageId: templatePage.id,
						pageType,
					});
				}
			} catch (err) {
				logger.error("Failed to import template page", {
					templateId,
					pageId: templatePage.id,
					pageType,
					err,
				});
				throw err;
			}
		}

		logger.info("Successfully imported template pages", {
			templateId,
			documentId,
			importedCount: createdPages.length,
		});

		return createdPages;
	}
);
$importTemplatePages.debugLabel = "importTemplatePages";
